import { useMemo, useState } from 'react';
import { Chip } from '@jotpsych/ui';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import { allergySeverityColor, allergySeverityLabel } from '../labels';
import { formatIsoDate } from '../vm/formatters';
import type { AllergyVM, NkaAssertionVM } from '../vm/types';

import { type ActiveFilter, ClinicalCardActions } from './ClinicalCardActions';
import { ClinicalCardFrame } from './ClinicalCardFrame';
import { RowActionsMenu } from './RowActionsMenu';

const COLUMNS = ['Allergen', 'Reaction', 'Severity', 'Onset', 'Status', ''] as const;

const headCellSx = {
  py: 1,
  fontWeight: 600,
  fontSize: 11,
  textTransform: 'uppercase' as const,
  letterSpacing: 0.6,
  color: 'text.secondary',
  bgcolor: 'backgroundColors.secondary',
  borderBottom: '1px solid',
  borderColor: 'divider',
};

const EM_DASH = '—';

interface AllergiesCardProps {
  allergies: AllergyVM[];
  /**
   * Explicit "no known allergies" assertion. When set (and no allergies exist),
   * the card shows the confirmed-NKA state — clinically distinct from an empty,
   * never-asked list. Backend sentinel: allergen_type='nka'; the generated API
   * types don't expose it yet, so live containers pass null until they do.
   */
  nka?: NkaAssertionVM | null;
  initialFilter?: ActiveFilter;
  /** Opens the add-allergy flow ("+ Add"). */
  onAdd?: () => void;
  /** Opens the edit flow for one allergy. */
  onEdit?: (allergyId: string) => void;
  /** Soft-inactivates an active allergy. */
  onInactivate?: (allergyId: string) => void;
  /** Reactivates an inactive allergy. */
  onReactivate?: (allergyId: string) => void;
  /** Notification for the host's analytics — fired after the filter changes. */
  onFilterChanged?: (filter: ActiveFilter) => void;
  /** 'embedded' hands the chrome and scroll to a dialog host. */
  variant?: 'card' | 'embedded';
  /** Embedded only: renders the header's close button (the dialog's X). */
  onClose?: () => void;
}

/**
 * Allergies card on the shared clinical-card pattern: severity as color-coded
 * chips (severe / life-threatening read as errors at a glance), status chip +
 * soft inactivation, and an explicit "No known allergies" state.
 * Presentational — renders AllergyVM[] and delegates every write to the host.
 */
export const AllergiesCard: React.FC<AllergiesCardProps> = ({
  allergies,
  nka = null,
  initialFilter = 'all',
  onAdd,
  onEdit,
  onInactivate,
  onReactivate,
  onFilterChanged,
  variant = 'card',
  onClose,
}) => {
  const [filter, setFilter] = useState<ActiveFilter>(initialFilter);

  const handleFilterChange = (value: ActiveFilter) => {
    setFilter(value);
    onFilterChanged?.(value);
  };

  const rows = useMemo(() => {
    if (filter === 'active') return allergies.filter((allergy) => allergy.isActive);
    if (filter === 'inactive') return allergies.filter((allergy) => !allergy.isActive);
    return [
      ...allergies.filter((allergy) => allergy.isActive),
      ...allergies.filter((allergy) => !allergy.isActive),
    ];
  }, [allergies, filter]);

  const showNka = nka && allergies.length === 0;

  return (
    <ClinicalCardFrame
      title="Allergies"
      variant={variant}
      onClose={onClose}
      actions={
        <ClinicalCardActions
          filter={filter}
          onFilterChange={handleFilterChange}
          filterLabel="Filter allergies"
          onAdd={onAdd}
        />
      }
    >
      {showNka ? (
        <Stack
          direction="row"
          spacing={1.5}
          sx={{
            alignItems: 'center',
            mx: 2,
            mb: 2,
            p: 2,
            borderRadius: 1.5,
            bgcolor: (theme) => alpha(theme.palette.success.main, 0.08),
          }}
        >
          <VerifiedOutlinedIcon sx={{ color: 'success.main' }} />
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              No known allergies
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Confirmed {formatIsoDate(nka.date)} · {nka.by}
            </Typography>
          </Box>
        </Stack>
      ) : rows.length === 0 ? (
        <Box sx={{ py: 5, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            No allergies recorded.
          </Typography>
          <Typography variant="caption" color="text.disabled">
            Not yet asked — record “No known allergies” or add an allergy.
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            overflowX: 'auto',
            ...(variant === 'embedded' && {
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
            }),
          }}
        >
          <Table size="small" stickyHeader={variant === 'embedded'} sx={{ minWidth: 640 }}>
          <TableHead>
            <TableRow>
              {COLUMNS.map((column, index) => (
                <TableCell
                  key={column || 'actions'}
                  align={index === COLUMNS.length - 1 ? 'right' : 'left'}
                  sx={{ ...headCellSx, width: index === 1 ? '30%' : undefined }}
                >
                  {column}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((allergy) => (
              <TableRow key={allergy.id}>
                <TableCell sx={{ py: 1.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {allergy.allergen}
                  </Typography>
                </TableCell>
                <TableCell sx={{ py: 1.5 }}>
                  <Typography
                    variant="body2"
                    color={allergy.reaction ? 'text.primary' : 'text.disabled'}
                  >
                    {allergy.reaction ?? EM_DASH}
                  </Typography>
                </TableCell>
                <TableCell sx={{ py: 1.5 }}>
                  {allergy.severity ? (
                    <Chip
                      variant="soft"
                      size="xSmall"
                      color={allergySeverityColor[allergy.severity]}
                      label={allergySeverityLabel[allergy.severity]}
                      sx={{ fontWeight: 600 }}
                    />
                  ) : (
                    <Typography variant="body2" color="text.disabled">
                      {EM_DASH}
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={{ py: 1.5 }}>
                  <Typography
                    variant="body2"
                    color={allergy.onsetDate ? 'text.primary' : 'text.disabled'}
                  >
                    {allergy.onsetDate ? formatIsoDate(allergy.onsetDate) : EM_DASH}
                  </Typography>
                </TableCell>
                <TableCell sx={{ py: 1.5 }}>
                  {allergy.isSuggested ? (
                    // Display-only: UpdateAllergyRequest carries no status
                    // field, so suggestions can't be accepted/dismissed from
                    // here yet.
                    <Chip variant="soft" size="xSmall" color="info" label="Suggested" />
                  ) : (
                    <Chip
                      variant="soft"
                      size="xSmall"
                      color={allergy.isActive ? 'success' : 'default'}
                      label={allergy.isActive ? 'Active' : 'Inactive'}
                    />
                  )}
                </TableCell>
                <TableCell align="right" sx={{ py: 1.5 }}>
                  <RowActionsMenu
                    ariaLabel={`Actions for ${allergy.allergen}`}
                    onSelect={(key) => {
                      if (key === 'edit') onEdit?.(allergy.id);
                      if (key === 'inactivate') onInactivate?.(allergy.id);
                      if (key === 'reactivate') onReactivate?.(allergy.id);
                    }}
                    actions={[
                      { key: 'edit', label: 'Edit', icon: <EditOutlinedIcon fontSize="small" /> },
                      allergy.isActive
                        ? {
                            key: 'inactivate',
                            label: 'Mark inactive',
                            icon: <BlockOutlinedIcon fontSize="small" />,
                          }
                        : {
                            key: 'reactivate',
                            label: 'Reactivate',
                            icon: <VerifiedOutlinedIcon fontSize="small" />,
                          },
                    ]}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          </Table>
        </Box>
      )}
    </ClinicalCardFrame>
  );
};
